// Per-item popup with a Calendar tab (count per day) and a Chart tab (count
// per day or, when the item carries a numeric value, an Intensity trend).
// Opened from the calendar icon on reportable Logs rows.

import { useEffect, useMemo, useRef, useState } from "react";
import type { ItemReportFeedbackContext, MedicalEvent, RegimenItem } from "@medassist/shared";
import {
  localCalendarDate,
  formatMedStrength,
  buildDoseTotals,
  buildSymptomActivity,
  buildOccurrenceStats,
  buildSymptomStats,
  buildComplianceStats,
  DEFAULT_COMPLIANCE_TOLERANCE_HOURS,
  symptomFollowUpKey,
  FAMILY_DISPLAY_UNIT,
  type DoseUnitFamily,
} from "@medassist/shared";
import { listEvents, getRegimens, submitFeedback, ApiError } from "../lib/api.js";
import { itemEvents, episodeStartEvents, type ItemIdentity } from "../lib/itemReport.js";
import { FeedbackThumbs } from "./FeedbackThumbs.js";
import { CountCalendar, OVERLAY_COLOR } from "./report/CountCalendar.js";
import { SymptomOverlayPicker, type OverlayOption } from "./report/SymptomOverlayPicker.js";
import { Select } from "./Select.js";
import { ReportRangeSelector, type RangeDays } from "./report/ReportRangeSelector.js";
import { CountChart } from "./report/CountChart.js";
import { downloadCsv } from "./report/exportCsv.js";
import { buildItemSeries } from "./report/itemSeries.js";
import { ItemStatsView, type ItemStatsDoseInput } from "./report/ItemStatsView.js";
import { ComplianceCard } from "./report/ComplianceCard.js";
import { LineChart } from "./charts/LineChart.js";
import { StatBadges } from "./charts/StatBadges.js";
import { ExportMenu } from "./charts/ExportMenu.js";
import { filterPoints } from "./charts/timeRanges.js";
import { EntryBody } from "./eventSummary.js";
import { ListSkeleton } from "./Skeleton.js";
import { ModalShell } from "./ModalShell.js";
import { TabBar } from "./TabBar.js";

export type Tab = "calendar" | "chart" | "stats";
type ChartMode = "count" | "dose" | "intensity";
type MedGranularity = "name" | "name+strength";

const DAY_MS = 24 * 60 * 60 * 1000;
const EPISODIC_TYPES = new Set(["symptom", "headache"]);

// D-IE-16: symptom/headache items default the Chart tab to Intensity.
const defaultChartMode = (it: ItemIdentity): ChartMode =>
  EPISODIC_TYPES.has(it.eventType) ? "intensity" : "count";

/** `rangeDays` local calendar dates ending today (inclusive), ascending —
 *  shared by chartDays and doseDays so the two windows can never drift. */
function windowDates(rangeDays: number, tz: string): string[] {
  const today = localCalendarDate(new Date().toISOString(), tz);
  const [y, m, d] = today.split("-").map(Number);
  const out: string[] = [];
  for (let i = rangeDays - 1; i >= 0; i--) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - i);
    out.push(dt.toISOString().slice(0, 10));
  }
  return out;
}

/** `rangeDays` local calendar dates immediately preceding `windowDates`'s
 *  window (D-IE-5's trend "previous window"), ascending. */
function prevWindowDates(rangeDays: number, tz: string): string[] {
  const start = windowDates(rangeDays, tz)[0];
  const [y, m, d] = start.split("-").map(Number);
  const out: string[] = [];
  for (let i = rangeDays; i >= 1; i--) {
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - i);
    out.push(dt.toISOString().slice(0, 10));
  }
  return out;
}

interface Props {
  patientId: string;
  patientName: string;
  timezone: string;
  item: ItemIdentity;
  autoCloseDays: number;
  /** Lateness tolerance (hours) from Patient prefs (D-MC-4); default 4. */
  complianceToleranceHours?: number;
  initialTab?: Tab;
  onClose: () => void;
}

export function ItemReportModal({
  patientId,
  patientName,
  timezone,
  item,
  autoCloseDays,
  complianceToleranceHours,
  initialTab = "calendar",
  onClose,
}: Props) {
  const [rawEvents, setRawEvents] = useState<MedicalEvent[] | null>(null);
  const [regimenItem, setRegimenItem] = useState<RegimenItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [chartMode, setChartMode] = useState<ChartMode>(() => defaultChartMode(item));
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const [selectedSeriesKey, setSelectedSeriesKey] = useState<string | null>(null);
  const [medGranularity, setMedGranularity] = useState<MedGranularity>("name");
  const [doseFamily, setDoseFamily] = useState<DoseUnitFamily | null>(null);
  const [doseIngredient, setDoseIngredient] = useState<string | null>(null);
  const [overlayKey, setOverlayKey] = useState<string | null>(null);
  const [overlaySource, setOverlaySource] = useState<MedicalEvent[] | null>(null);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // Load events once on open (D-IR-8: fetch every type the item folds in, e.g.
  // both medication_taken and as_needed_med for a medication item).
  const fetchTypesKey = item.fetchTypes.join(",");
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(item.fetchTypes.map((t) => listEvents(patientId, t)))
      .then((results) => {
        if (!cancelled) setRawEvents(results.flat());
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : "Failed to load events");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, fetchTypesKey]);

  // D-MC-3: compliance needs the medication's RegimenItem (for its schedule).
  // Fetched lazily since it's only relevant for medication items.
  useEffect(() => {
    if (item.eventType !== "medication_taken") {
      setRegimenItem(null);
      return;
    }
    let cancelled = false;
    const nameLower = item.label.trim().toLowerCase();
    getRegimens(patientId)
      .then((regimens) => {
        if (cancelled) return;
        const found = regimens
          .flatMap((r) => r.items)
          .find((it) => it.name.trim().toLowerCase() === nameLower);
        setRegimenItem(found ?? null);
      })
      .catch(() => {
        if (!cancelled) setRegimenItem(null);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, item.eventType, item.label]);

  const filtered = useMemo(
    () => (rawEvents ? itemEvents(item, rawEvents) : []),
    [rawEvents, item]
  );

  // D-IR-9: medication granularity toggle narrows the already-fetched/merged
  // events to the strength of the dose that opened the modal.
  const scoped = useMemo(() => {
    if (medGranularity === "name+strength" && item.medStrength) {
      return filtered.filter(
        (e) => formatMedStrength(e.payload as Parameters<typeof formatMedStrength>[0]) === item.medStrength
      );
    }
    return filtered;
  }, [filtered, medGranularity, item.medStrength]);

  // D-IR-7: symptom/headache items count distinct episodes, not every
  // follow-up entry. Other types are unaffected (every event is its own
  // "episode"). Intensity mode always plots every point regardless.
  const isEpisodic = EPISODIC_TYPES.has(item.eventType);
  const countableEvents = useMemo(() => {
    if (!isEpisodic) return scoped;
    const sorted = [...scoped].sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
    const starts = episodeStartEvents(sorted, autoCloseDays);
    return sorted.filter((e) => starts.has(e.eventId));
  }, [scoped, isEpisodic, autoCloseDays]);

  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Intensity series for this item (D-IR-3); empty => no Intensity mode (D-IR-4).
  // Symptom/headache items build a daily-resampled activity series (D-IE-15),
  // which needs the patient timezone + auto-close horizon.
  const series = useMemo(
    () => buildItemSeries(item, scoped, { timezone: tz, autoCloseDays }),
    [item, scoped, tz, autoCloseDays]
  );
  const hasIntensity = series.length > 0;

  // Reset the sub-metric selection, granularity toggle, chart mode, and dose
  // family/ingredient selection when the item changes.
  useEffect(() => {
    setSelectedSeriesKey(null);
    setMedGranularity("name");
    setChartMode(defaultChartMode(item));
    setDoseFamily(null);
    setDoseIngredient(null);
    setOverlayKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.key]);

  // Fall back to Count mode if the item has no numeric series. Gated on
  // !loading: series is necessarily empty before the fetch resolves, so
  // checking this while loading would immediately clobber the D-IE-16
  // symptom/headache "default to Intensity" choice before data ever arrives.
  useEffect(() => {
    if (!loading && !hasIntensity && chartMode === "intensity") setChartMode("count");
  }, [loading, hasIntensity, chartMode]);

  // Total dose/day mode (D-IE-7/8/9): summed per-day dose amounts, one unit
  // family at a time, never mixing ingredients of a combination product.
  const isMedication = item.eventType === "medication_taken";
  const doseTotals = useMemo(
    () => (isMedication ? buildDoseTotals(scoped, tz, doseIngredient ?? undefined) : null),
    [isMedication, scoped, tz, doseIngredient]
  );
  const hasDoseMode = isMedication && !!doseTotals && doseTotals.families.length > 0;
  const effectiveFamily: DoseUnitFamily | null = doseFamily ?? doseTotals?.families[0]?.family ?? null;

  // Default ingredient for a combination product with no top-level doses.
  useEffect(() => {
    if (
      doseTotals &&
      !doseTotals.hasTopLevelDoses &&
      doseIngredient === null &&
      doseTotals.ingredientNames.length > 0
    ) {
      setDoseIngredient(doseTotals.ingredientNames[0]);
    }
  }, [doseTotals, doseIngredient]);

  // Fall back to Count mode if dose mode is no longer available (e.g. the
  // med-granularity toggle narrowed events to a strength with no dose data).
  useEffect(() => {
    if (!hasDoseMode && chartMode === "dose") setChartMode("count");
  }, [hasDoseMode, chartMode]);

  const excludedNote = useMemo(() => {
    if (!doseTotals || !effectiveFamily) return null;
    const otherFamilies = doseTotals.families.filter((f) => f.family !== effectiveFamily);
    const otherDoseCount = otherFamilies.reduce((a, f) => a + f.doseCount, 0);
    const skipped = otherDoseCount + doseTotals.skippedEventCount;
    if (skipped === 0) return null;
    const reasons: string[] = [];
    if (otherFamilies.length > 0) {
      reasons.push(otherFamilies.map((f) => FAMILY_DISPLAY_UNIT[f.family]).join(", "));
    }
    if (doseTotals.skippedEventCount > 0) reasons.push("no dose recorded");
    return `${skipped} dose${skipped === 1 ? "" : "s"} not shown (${reasons.join("; ")})`;
  }, [doseTotals, effectiveFamily]);

  const selectedSeries = useMemo(
    () => series.find((s) => s.key === selectedSeriesKey) ?? series[0] ?? null,
    [series, selectedSeriesKey]
  );

  // How many days back the selected series actually has data, so the Chart
  // tab can explain why switching to a longer range shows the same plot
  // (e.g. a symptom only ever logged over the last 4 days).
  const dataSpanDays = useMemo(() => {
    if (!selectedSeries || selectedSeries.points.length === 0) return null;
    const earliest = Math.min(...selectedSeries.points.map((p) => p.x));
    return Math.max(1, Math.ceil((Date.now() - earliest) / DAY_MS));
  }, [selectedSeries]);

  // Windowed points for the selected series + the equal-length prior window
  // (for StatBadges' "vs prev" delta).
  const seriesWindow = useMemo(() => {
    if (!selectedSeries) return null;
    const end = Date.now();
    const start = end - rangeDays * DAY_MS;
    const prevStart = start - rangeDays * DAY_MS;
    return {
      points: filterPoints(selectedSeries.points, start, end),
      prevPoints: filterPoints(selectedSeries.points, prevStart, start),
    };
  }, [selectedSeries, rangeDays]);

  // Build count map: yyyy-mm-dd -> count. D-IR-7: episode starts only for
  // symptom/headache items (countableEvents already narrows to those).
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of countableEvents) {
      const d = localCalendarDate(e.occurredAt, timezone);
      m.set(d, (m.get(d) ?? 0) + 1);
    }
    return m;
  }, [countableEvents, timezone]);

  // D-IE-14: symptom/headache calendars mark every active day (onset through
  // resolution), not just onset days — an activity layer alongside the
  // episode-start `counts` above (which stays the correct unit for Count mode).
  const symptomActivity = useMemo(
    () => (isEpisodic ? buildSymptomActivity(scoped, tz, autoCloseDays) : null),
    [isEpisodic, scoped, tz, autoCloseDays]
  );

  // Day drill-down: every entry for the day (not just episode starts), oldest first
  const dayEvents = useMemo(() => {
    if (!selectedDay) return [];
    return scoped
      .filter((e) => localCalendarDate(e.occurredAt, timezone) === selectedDay)
      .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));
  }, [scoped, selectedDay, timezone]);

  // Ongoing-day drill-down (Task 3.1): a click on an active day with no
  // events of its own (a carried-forward day) shows the governing check-in —
  // the most recent in-span check-in on or before the clicked date.
  const governingCheckIn = useMemo(() => {
    if (!selectedDay || !symptomActivity) return null;
    const span = symptomActivity.spans.find(
      (s) => selectedDay >= s.startDate && selectedDay <= s.endDate
    );
    if (!span) return null;
    let best: { date: string; severity: number } | null = null;
    for (const e of scoped) {
      if (e.eventType !== "symptom" && e.eventType !== "headache") continue;
      const p = e.payload as Record<string, unknown>;
      const severity = typeof p.severity === "number" ? p.severity : undefined;
      if (severity === undefined || severity < 1) continue;
      const d = localCalendarDate(e.occurredAt, tz);
      if (d < span.startDate || d > selectedDay) continue;
      if (!best || d >= best.date) best = { date: d, severity };
    }
    return best;
  }, [selectedDay, symptomActivity, scoped, tz]);

  // Per-day average of the primary metric, for calendar tinting (D-IR-6 polish).
  // Limited to 0-10 rated metrics (unit "/10") so the band colors stay meaningful —
  // vitals/hydration/etc. use unrelated scales and are left untinted.
  const intensityByDay = useMemo(() => {
    const primary = series[0];
    if (!primary || primary.unit !== "/10") return undefined;
    const sums = new Map<string, { total: number; n: number }>();
    for (const pt of primary.points) {
      const d = localCalendarDate(new Date(pt.x).toISOString(), tz);
      const cur = sums.get(d) ?? { total: 0, n: 0 };
      cur.total += pt.y;
      cur.n += 1;
      sums.set(d, cur);
    }
    const out = new Map<string, number>();
    for (const [d, { total, n }] of sums) out.set(d, total / n);
    return out;
  }, [series, tz]);

  // Zero-filled per-day counts for the selected window (timezone-aware)
  const chartDays = useMemo(
    () => windowDates(rangeDays, tz).map((date) => ({ date, count: counts.get(date) ?? 0 })),
    [counts, rangeDays, tz]
  );

  // Zero-filled per-day dose totals for the selected window + effective family.
  const doseDays = useMemo(() => {
    const map = effectiveFamily ? doseTotals?.byFamily.get(effectiveFamily) : undefined;
    return windowDates(rangeDays, tz).map((date) => ({ date, count: map?.get(date) ?? 0 }));
  }, [doseTotals, effectiveFamily, rangeDays, tz]);

  // Stats tab (D-IE-1..3, Task 4.2): occurrence stats computed over the
  // countable events (episode starts for symptom/headache, D-IE-5), so Stats
  // and the Chart's Count bars agree.
  const occurrenceStats = useMemo(
    () => buildOccurrenceStats(countableEvents, tz, rangeDays),
    [countableEvents, tz, rangeDays]
  );

  const round2 = (n: number) => Math.round(n * 100) / 100;

  // Dose amounts card input (medication only): derived from the same
  // window-clipped doseDays + effective family used by the Total dose/day
  // chart, so the two numbers reconcile (D-IR verification requirement).
  const doseStatsInput = useMemo<ItemStatsDoseInput | null>(() => {
    if (!isMedication || !effectiveFamily || !doseTotals) return null;
    const total = round2(doseDays.reduce((a, d) => a + d.count, 0));
    const loggedDays = doseDays.filter((d) => d.count > 0);
    const daysLogged = loggedDays.length;
    const avgPerLoggedDay = daysLogged > 0 ? round2(total / daysLogged) : 0;
    const avgPerCalendarDay = round2(total / rangeDays);
    let peakDay: string | null = null;
    let peakAmount = 0;
    for (const d of doseDays) {
      if (d.count > 0 && d.count >= peakAmount) {
        peakAmount = d.count;
        peakDay = d.date;
      }
    }
    const map = doseTotals.byFamily.get(effectiveFamily);
    let prevTotal: number | null = null;
    if (map) {
      const prevDates = prevWindowDates(rangeDays, tz);
      const sum = prevDates.reduce((a, d) => a + (map.get(d) ?? 0), 0);
      prevTotal = sum > 0 ? round2(sum) : null;
    }
    return {
      displayUnit: FAMILY_DISPLAY_UNIT[effectiveFamily],
      total,
      avgPerLoggedDay,
      avgPerCalendarDay,
      peakDay,
      peakAmount,
      prevTotal,
      excludedNote,
    };
  }, [isMedication, effectiveFamily, doseTotals, doseDays, rangeDays, tz, excludedNote]);

  // Compliance card input (medication only, D-MC-1..3): null when the item
  // has no active schedule (buildComplianceStats returns null in that case).
  const complianceStats = useMemo(() => {
    if (!isMedication) return null;
    return buildComplianceStats(
      scoped,
      regimenItem,
      windowDates(rangeDays, tz),
      complianceToleranceHours ?? DEFAULT_COMPLIANCE_TOLERANCE_HOURS,
      tz
    );
  }, [isMedication, scoped, regimenItem, rangeDays, tz, complianceToleranceHours]);

  // Episodes card input (symptom/headache only): reuses the same
  // buildSymptomStats module as the Reports-page Symptom stats tab.
  const episodesStatsInput = useMemo(() => {
    if (!isEpisodic) return null;
    const key =
      item.eventType === "headache" ? symptomFollowUpKey("headache") : symptomFollowUpKey(item.label);
    const end = Date.now();
    const start = end - rangeDays * DAY_MS;
    const rows = buildSymptomStats(scoped, start, end);
    return rows.find((r) => r.key === key) ?? null;
  }, [isEpisodic, scoped, rangeDays, item.eventType, item.label]);

  // D-IE-13: overlay symptom/headache events are fetched lazily, on first
  // entry to the Calendar tab — never on the Chart-only path.
  //
  // overlayLoading is intentionally NOT a dependency here: setOverlayLoading(true)
  // below would otherwise re-run this effect, whose cleanup cancels the fetch
  // that's still in flight — the resulting `cancelled` guard then blocks the
  // .then/.finally handlers from ever calling setOverlayLoading(false), so the
  // dropdown gets stuck on "Loading..." forever.
  useEffect(() => {
    if (tab !== "calendar" || overlaySource !== null || overlayLoading) return;
    let cancelled = false;
    setOverlayLoading(true);
    Promise.all([listEvents(patientId, "symptom"), listEvents(patientId, "headache")])
      .then((results) => {
        if (cancelled) return;
        setOverlaySource(results.flat().filter((e) => !e.deleted));
      })
      .catch(() => {
        // An overlay is a nice-to-have; never break the calendar on failure.
        if (!cancelled) setOverlaySource([]);
      })
      .finally(() => {
        if (!cancelled) setOverlayLoading(false);
      });
    // If this effect is torn down before the fetch resolves (e.g. the user
    // leaves the Calendar tab), reset overlayLoading too — not just the
    // cancelled flag — so a later re-entry to the tab (overlaySource is
    // still null) retries the fetch instead of finding overlayLoading stuck
    // true forever from a fetch whose own callbacks got skipped.
    return () => {
      cancelled = true;
      setOverlayLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, patientId, overlaySource]);

  // D-IE-10: overlay source list = symptom + headache identities, own
  // identity excluded when this item itself is a symptom/headache.
  const ownOverlayKey =
    item.eventType === "symptom"
      ? symptomFollowUpKey(item.label)
      : item.eventType === "headache"
        ? symptomFollowUpKey("headache")
        : null;

  const overlayOptions = useMemo<OverlayOption[]>(() => {
    if (!overlaySource) return [];
    const byKey = new Map<string, { label: string; lastAt: string }>();
    for (const e of overlaySource) {
      const p = e.payload as Record<string, unknown>;
      const rawName = e.eventType === "headache" ? "headache" : String(p.name ?? "symptom");
      const key = symptomFollowUpKey(rawName);
      if (key === ownOverlayKey) continue;
      const existing = byKey.get(key);
      if (!existing || Date.parse(e.occurredAt) > Date.parse(existing.lastAt)) {
        byKey.set(key, { label: rawName, lastAt: e.occurredAt });
      }
    }
    return Array.from(byKey.entries())
      .map(([key, v]) => ({ key, label: v.label, lastAt: v.lastAt }))
      .sort((a, b) => Date.parse(b.lastAt) - Date.parse(a.lastAt));
  }, [overlaySource, ownOverlayKey]);

  // Reset the overlay selection if it's no longer in the option list (e.g.
  // the source refetched, or the item changed and the key no longer applies).
  useEffect(() => {
    if (overlayKey && overlaySource && !overlayOptions.some((o) => o.key === overlayKey)) {
      setOverlayKey(null);
    }
  }, [overlayKey, overlaySource, overlayOptions]);

  const overlayActivity = useMemo(() => {
    if (!overlayKey || !overlaySource) return null;
    const events = overlaySource.filter((e) => {
      const p = e.payload as Record<string, unknown>;
      const rawName = e.eventType === "headache" ? "headache" : String(p.name ?? "symptom");
      return symptomFollowUpKey(rawName) === overlayKey;
    });
    return buildSymptomActivity(events, tz, autoCloseDays);
  }, [overlayKey, overlaySource, tz, autoCloseDays]);

  const overlayOption = overlayOptions.find((o) => o.key === overlayKey) ?? null;
  const overlay = overlayOption && overlayActivity
    ? { label: overlayOption.label, color: OVERLAY_COLOR, activeDays: overlayActivity.severityByDay }
    : undefined;

  function buildFeedbackContext(): ItemReportFeedbackContext {
    return {
      kind: "item_report",
      itemKey: item.key,
      itemLabel: item.label,
      eventType: item.eventType,
      tab,
      rangeDays,
      autoCloseDays,
      events: scoped.map((e) => ({ eventId: e.eventId, occurredAt: e.occurredAt, payload: e.payload })),
    };
  }

  async function handleFeedbackSubmit(
    rating: "up" | "down",
    opts: { reasonText?: string }
  ) {
    await submitFeedback({
      patientId,
      sessionId: `item-report-${item.key}`,
      mode: "capture",
      rating,
      ...(rating === "down" ? { reasonCategory: "inaccurate" as const, reasonText: opts.reasonText } : {}),
      pendingProposal: buildFeedbackContext(),
    });
  }

  const subheader = (
    <>
      {/* Tab bar */}
      <TabBar
        ariaLabel="Report views"
        tabs={[
          { id: "calendar", label: "Calendar" },
          { id: "chart", label: "Chart" },
          { id: "stats", label: "Stats" },
        ]}
        active={tab}
        onSelect={setTab}
      />

      {/* Medication granularity toggle (D-IR-9) */}
      {item.medStrength && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Group by:</span>
          <div className="flex rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
            {(["name", "name+strength"] as MedGranularity[]).map((g) => (
              <button
                key={g}
                onClick={() => setMedGranularity(g)}
                className={[
                  "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                  medGranularity === g
                    ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                ].join(" ")}
              >
                {g === "name" ? "By name" : `By name+strength (${item.medStrength})`}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <ModalShell
      title={`${item.label} — ${patientName}`}
      size="2xl"
      subheader={subheader}
      onClose={onClose}
    >
      {loading && <ListSkeleton rows={4} lines={1} />}

          {!loading && error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {!loading && !error && tab === "calendar" && (
            <>
              {scoped.length === 0 ? (
                <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">
                  No entries recorded for {item.label}.
                </p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <SymptomOverlayPicker
                      options={overlayOptions}
                      value={overlayKey}
                      onChange={setOverlayKey}
                      loading={overlayLoading}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (overlay) {
                          const dates = Array.from(
                            new Set([...counts.keys(), ...overlay.activeDays.keys()])
                          ).sort((a, b) => a.localeCompare(b));
                          downloadCsv(
                            ["date", "count", `${overlay.label} severity`],
                            dates.map((d) => [d, counts.get(d) ?? 0, overlay.activeDays.get(d) ?? ""]),
                            `${item.label}-calendar`
                          );
                        } else {
                          downloadCsv(
                            ["date", "count"],
                            Array.from(counts.entries()).sort(([a], [b]) => a.localeCompare(b)),
                            `${item.label}-calendar`
                          );
                        }
                      }}
                      className="min-h-[44px] rounded-md border border-gray-300 px-3 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:min-h-0 sm:py-1.5"
                    >
                      Export CSV
                    </button>
                  </div>
                  <CountCalendar
                    counts={counts}
                    intensityByDay={intensityByDay}
                    activeDays={symptomActivity?.severityByDay}
                    overlay={overlay}
                    timezone={tz}
                    onDayClick={(date) =>
                      setSelectedDay((prev) => (prev === date ? null : date))
                    }
                  />

                  {selectedDay && (dayEvents.length > 0 || governingCheckIn) && (
                    <div className="mt-4 border-t border-gray-200 dark:border-gray-800 pt-4">
                      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {new Date(selectedDay + "T12:00:00Z").toLocaleDateString(undefined, {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          timeZone: "UTC",
                        })}
                      </h3>
                      {dayEvents.length > 0 ? (
                        <ul className="space-y-1.5">
                          {dayEvents.map((e) => {
                            const time = new Date(e.occurredAt).toLocaleTimeString(undefined, {
                              hour: "numeric",
                              minute: "2-digit",
                            });
                            return (
                              <li
                                key={e.eventId}
                                className="flex items-center gap-2 rounded-lg border border-gray-100 dark:border-gray-800 px-3 py-2 text-sm"
                              >
                                <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500 w-14">
                                  {time}
                                </span>
                                <EntryBody e={e} />
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        governingCheckIn && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Ongoing — last recorded severity {governingCheckIn.severity}/10 on{" "}
                            {new Date(governingCheckIn.date + "T12:00:00Z").toLocaleDateString(undefined, {
                              month: "long",
                              day: "numeric",
                              timeZone: "UTC",
                            })}
                            .
                          </p>
                        )
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {!loading && !error && tab === "chart" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
                  <button
                    onClick={() => setChartMode("count")}
                    className={[
                      "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                      chartMode === "count"
                        ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                    ].join(" ")}
                  >
                    Count per day
                  </button>
                  {hasDoseMode && (
                    <button
                      onClick={() => setChartMode("dose")}
                      className={[
                        "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                        chartMode === "dose"
                          ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                      ].join(" ")}
                    >
                      Total dose/day
                    </button>
                  )}
                  {hasIntensity && (
                    <button
                      onClick={() => setChartMode("intensity")}
                      className={[
                        "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                        chartMode === "intensity"
                          ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                      ].join(" ")}
                    >
                      Intensity
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <ReportRangeSelector value={rangeDays} onChange={setRangeDays} />
                  {chartMode !== "intensity" && (
                    <button
                      type="button"
                      onClick={() =>
                        chartMode === "dose" && effectiveFamily
                          ? downloadCsv(
                              ["date", FAMILY_DISPLAY_UNIT[effectiveFamily]],
                              doseDays.map((d) => [d.date, d.count]),
                              `${item.label}-dose-${rangeDays}d`
                            )
                          : downloadCsv(
                              ["date", "count"],
                              chartDays.map((d) => [d.date, d.count]),
                              `${item.label}-count-${rangeDays}d`
                            )
                      }
                      className="min-h-[44px] rounded-md border border-gray-300 px-3 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:min-h-0 sm:py-1.5"
                    >
                      Export CSV
                    </button>
                  )}
                  {chartMode === "intensity" && selectedSeries && seriesWindow && (
                    <ExportMenu
                      svgRef={svgRef}
                      series={[{ ...selectedSeries, points: seriesWindow.points }]}
                      filename={`${item.label}-intensity`}
                    />
                  )}
                </div>
              </div>

              {chartMode === "intensity" && series.length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  {series.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSelectedSeriesKey(s.key)}
                      className={[
                        "min-h-[44px] rounded-full px-3 text-xs font-medium transition-colors sm:min-h-0 sm:py-1",
                        selectedSeries?.key === s.key
                          ? "bg-[color:var(--accent,#2563eb)] text-white"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700",
                      ].join(" ")}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}

              {chartMode === "dose" && doseTotals && (
                <div className="flex flex-wrap items-center gap-3">
                  {doseTotals.families.length > 1 && (
                    <div className="flex flex-wrap rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
                      {doseTotals.families.map((f) => (
                        <button
                          key={f.family}
                          onClick={() => setDoseFamily(f.family)}
                          className={[
                            "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
                            effectiveFamily === f.family
                              ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
                              : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
                          ].join(" ")}
                        >
                          {f.displayUnit} ({f.doseCount})
                        </button>
                      ))}
                    </div>
                  )}
                  {doseTotals.ingredientNames.length > 0 && (
                    <Select
                      variant="dense"
                      value={doseIngredient ?? ""}
                      onChange={(e) => {
                        setDoseIngredient(e.target.value || null);
                        setDoseFamily(null);
                      }}
                      className="min-h-[44px] sm:min-h-0"
                    >
                      {doseTotals.hasTopLevelDoses && <option value="">This medication</option>}
                      {doseTotals.ingredientNames.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  )}
                  {excludedNote && (
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{excludedNote}</span>
                  )}
                </div>
              )}

              {chartMode === "count" && <CountChart days={chartDays} />}

              {chartMode === "dose" && effectiveFamily && (
                <CountChart
                  days={doseDays}
                  valueFormat={(n) => n.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  valueUnit={FAMILY_DISPLAY_UNIT[effectiveFamily]}
                  emptyHint="No doses with a recorded amount in this range."
                />
              )}

              {chartMode === "intensity" && selectedSeries && seriesWindow && (
                <div className="space-y-3">
                  {dataSpanDays !== null && dataSpanDays < rangeDays && (
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      Only {dataSpanDays} day{dataSpanDays === 1 ? "" : "s"} of data available — the last{" "}
                      {rangeDays}-day range looks the same as shorter ones until more is logged.
                    </p>
                  )}
                  <LineChart
                    series={[{ ...selectedSeries, points: seriesWindow.points }]}
                    timezone={tz}
                    svgRef={svgRef}
                    refLow={selectedSeries.refLow}
                    refHigh={selectedSeries.refHigh}
                    emptyHint="No data in this range."
                  />
                  <StatBadges
                    points={seriesWindow.points}
                    unit={selectedSeries.unit}
                    prevPoints={seriesWindow.prevPoints}
                  />
                </div>
              )}
            </div>
          )}

          {!loading && !error && tab === "stats" && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <ReportRangeSelector value={rangeDays} onChange={setRangeDays} />
                <button
                  type="button"
                  onClick={() => {
                    const rows: [string, string | number][] = [
                      ["Total occurrences", occurrenceStats.totalOccurrences],
                      ["Days logged", `${occurrenceStats.daysLogged} of ${occurrenceStats.windowDays}`],
                      ["Pct days logged", occurrenceStats.pctDaysLogged],
                      ["Avg per logged day", occurrenceStats.avgPerLoggedDay],
                      ["Avg interval days", occurrenceStats.avgIntervalDays ?? ""],
                      ["Days since last", occurrenceStats.daysSinceLast ?? ""],
                      ["Longest gap days", occurrenceStats.longestZeroRunDays],
                      ["Max in one day", occurrenceStats.maxInOneDay],
                      ["Peak day", occurrenceStats.peakDay ?? ""],
                      ["Streak count", occurrenceStats.streaks.count],
                      ["Longest streak", occurrenceStats.streaks.longest],
                      ["Avg streak length", occurrenceStats.streaks.avgLength],
                      ["Current streak", occurrenceStats.streaks.currentLength],
                      ["Avg gap between streaks", occurrenceStats.streaks.avgGapDays ?? ""],
                      ["Isolated single days", occurrenceStats.streaks.singleDayCount],
                      ["Most common hour", occurrenceStats.mostCommonHour ?? ""],
                      ...(doseStatsInput
                        ? ([
                            ["Dose total", doseStatsInput.total],
                            ["Dose avg per logged day", doseStatsInput.avgPerLoggedDay],
                            ["Dose avg per calendar day", doseStatsInput.avgPerCalendarDay],
                            ["Dose peak day", doseStatsInput.peakDay ?? ""],
                            ["Dose peak amount", doseStatsInput.peakAmount],
                            ["Dose prev total", doseStatsInput.prevTotal ?? ""],
                          ] as [string, string | number][])
                        : []),
                      ...(episodesStatsInput
                        ? ([
                            ["Episodes", episodesStatsInput.sessionCount],
                            ["Resolved episodes", episodesStatsInput.resolvedSessionCount],
                            ["Ongoing episodes", episodesStatsInput.ongoingSessionCount],
                            ["Resolved pct", episodesStatsInput.resolvedPct],
                            ["Avg resolution ms", episodesStatsInput.avgResolutionMs ?? ""],
                            ["Min resolution ms", episodesStatsInput.minResolutionMs ?? ""],
                            ["Max resolution ms", episodesStatsInput.maxResolutionMs ?? ""],
                            ["Avg severity", episodesStatsInput.avgSeverity],
                            ["Max severity", episodesStatsInput.maxSeverity],
                            ["Days with check-in", episodesStatsInput.distinctDaysLogged],
                          ] as [string, string | number][])
                        : []),
                    ];
                    downloadCsv(["stat", "value"], rows, `${item.label}-stats-${rangeDays}d`);
                  }}
                  className="min-h-[44px] rounded-md border border-gray-300 px-3 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 sm:min-h-0 sm:py-1.5"
                >
                  Export CSV
                </button>
              </div>
              {isMedication && (
                <ComplianceCard stats={complianceStats} medicationName={item.label} windowDays={rangeDays} />
              )}
              <ItemStatsView
                stats={occurrenceStats}
                rangeDays={rangeDays}
                itemLabel={item.label}
                dose={doseStatsInput ?? undefined}
                episodes={episodesStatsInput}
              />
            </div>
          )}

          {!loading && !error && (
            <FeedbackThumbs
              prompt="Does this report look right?"
              placeholder="What's wrong — missing entries, wrong counts, bad chart? (optional)"
              onSubmit={handleFeedbackSubmit}
            />
          )}
    </ModalShell>
  );
}
